import axios from 'axios';
import { environment } from '../../environments/environment';

/**
 * A campaign capsule's persisted flow output, as shown on the Insights tab.
 *
 * Read in two steps: a paged `summary` view of every group (one row each), then
 * the full items of a single group when a row is opened. The `{ status, data }`
 * envelope is unwrapped here so callers only ever see rows and items.
 */

const getBaseUrl = () => `${environment.controlplaneUrl}/agent/flow/output`;

const getHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
};

/* ------------------------------------------------------------------- types */

export interface UserReviewOption {
    id: string;
    label: string;
    current: boolean;
    description?: string;
}

export interface DictionaryEntry {
    key?: string;
    label: string;
    value: any;
}

export interface MultiColumnDictValue {
    headers: string[];
    data: any[];
    title?: string;
    metadata?: {
        visualizationtype?: string;
        visualizationType?: string;
        title?: string;
        x_axis_label?: string;
        y_axis_label?: string;
        bubble_label?: string;
        bubble_size_param?: string;
        x_axis_param?: string;
        y_axis_param?: string;
    };
}

export interface Item {
    objectType: 'DICTIONARY' | 'MULTICOLUMNDICT';
    key?: string;
    label?: string;
    chart_type?: string;
    docUID?: string;
    metadata?: {
        visualizationType?: string;
        visualizationtype?: string;
        title?: string;
        x_axis_label?: string;
        y_axis_label?: string;
        bubble_label?: string;
        x_axis_param?: string;
        y_axis_param?: string;
    };
    entries?: DictionaryEntry[];
    value?: MultiColumnDictValue;
}

/** One group of output — a case, in the AML flows. */
export interface DataElement {
    groupName: string;
    /** Present when the platform lifts it out of the items; otherwise read from the entries. */
    groupIdentifier?: string;
    items: Item[];
}

export interface OutputScope {
    campaignId: string;
    capsuleId: string;
}

export interface SummaryPage {
    rows: DataElement[];
    /** Total groups across all pages; null when the platform doesn't report it. */
    total: number | null;
}

/** What `/output/insight` reports about the insight itself, beside its items. */
export interface InsightMeta {
    insightId?: string;
    groupIdentifier?: string;
    groupName?: string;
    title?: string;
    informationCategory?: string;
    /** Where the insight was produced, so it can be refreshed and explained like an Insights row. */
    campaignId?: string;
    capsuleId?: string;
    /** The step that produced the insight, read from its items. */
    activityCode?: string;
}

/** One insight, as an agent reference opens it. */
export interface InsightDetail {
    items: Item[];
    meta: InsightMeta;
}

/* ----------------------------------------------------------------- helpers */

/** The identifier `/output/group` is addressed by, or null when a row carries none. */
export function groupIdentifierOf(row: DataElement): string | null {
    if (row.groupIdentifier) return String(row.groupIdentifier);
    for (const item of row.items ?? []) {
        const entry = item.entries?.find(e => e.key === 'groupIdentifier');
        if (entry && entry.value !== null && entry.value !== undefined && entry.value !== '') {
            return String(entry.value);
        }
    }
    return null;
}

const asCount = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

/** The total group count, from whichever field the response carries it in. */
export function totalOf(body: any): number | null {
    return asCount(body?.pagination?.totalGroups) ?? asCount(body?.pagination?.total)
        ?? asCount(body?.total) ?? asCount(body?.totalCount);
}

/** Paging bookkeeping the summary view returns; never shown to the user. */
export const PAGING_KEYS: ReadonlySet<string> = new Set([
    'pageNumber', 'pageSize', 'totalGroups', 'totalPages', 'recordCount', 'ungroupedRecordCount',
]);

/**
 * Where and when a group's output was produced — plumbing for the platform, not
 * findings, and never shown to the user.
 */
export const RUN_BOOKKEEPING_KEYS: ReadonlySet<string> = new Set([
    'snapshotKey', 'runSessionId', 'nodeExecutionId', 'activityCode', 'isCurrent',
]);

/** Everything the summary view reports about the page rather than a group. */
const PAGE_INFO_KEYS = new Set([...PAGING_KEYS, 'truncated', 'sortBy', 'sortOrder', 'code', 'label']);

const itemKeys = (item: Item): string[] =>
    item.entries ? item.entries.map(e => e.key ?? e.label) : [item.key ?? ''];

/**
 * Whether a row is the summary view's paging and sort info rather than a group.
 * Without the platform lifting it out of the rows, each of those fields arrives
 * as its own item in an 'Other' group with no identifier.
 */
export function isPageInfoRow(row: DataElement): boolean {
    return !groupIdentifierOf(row) && (row.items ?? []).length > 0
        && row.items.every(item => itemKeys(item).every(key => PAGE_INFO_KEYS.has(key)));
}

/** A count reported inside the page-info rows, e.g. totalGroups. */
const pageInfoCount = (rows: DataElement[], key: string): number | null => {
    for (const row of rows) {
        for (const item of row.items ?? []) {
            const value = asCount(item.entries?.find(e => e.key === key)?.value);
            if (value !== null) return value;
        }
    }
    return null;
};

const isGroup = (value: any): value is DataElement =>
    Boolean(value) && Array.isArray(value.items) && !('objectType' in value);

/**
 * A group's items. The endpoint returns the items themselves, but a grouped
 * `[{ groupName, items }]` body is accepted too, in case it mirrors `/output`.
 */
export function itemsOf(data: unknown): Item[] {
    if (!Array.isArray(data)) return [];
    return data.flatMap((entry: any) => (isGroup(entry) ? entry.items : [entry as Item]));
}

type InsightMetaKey = Exclude<keyof InsightMeta, 'activityCode'>;
const INSIGHT_META_KEYS: ReadonlySet<string> = new Set<InsightMetaKey>([
    'insightId', 'groupIdentifier', 'groupName', 'title', 'informationCategory', 'campaignId', 'capsuleId',
]);
/** The keys a DICTIONARY may hold and still be a descriptor rather than content. */
const DESCRIPTOR_DICTIONARY_KEYS: ReadonlySet<string> = new Set<InsightMetaKey>([
    'insightId', 'informationCategory', 'campaignId', 'capsuleId',
]);

const asText = (value: unknown): string | undefined =>
    value === null || value === undefined || value === '' ? undefined : String(value);

/**
 * Splits an `/output/insight` body into the items to show and the insight's own
 * descriptors. The descriptors arrive as items of their own — top-level
 * IDENTIFIER / PARAGRAPH items, and one-entry DICTIONARYs holding
 * informationCategory, campaignId or capsuleId — which the item renderer would
 * otherwise draw as stray, untitled tables.
 */
export function insightOf(data: unknown): InsightDetail {
    const meta: InsightMeta = {};
    const note = (key: keyof InsightMeta, value: unknown) => {
        if (meta[key] === undefined) meta[key] = asText(value);
    };
    const items: Item[] = [];
    for (const item of itemsOf(data) as any[]) {
        const entries: any[] | undefined = Array.isArray(item?.entries) ? item.entries : undefined;
        if (item?.objectType === 'DICTIONARY' && entries?.every(e => DESCRIPTOR_DICTIONARY_KEYS.has(e.key))) {
            entries.forEach(e => note(e.key, e.value));
        } else if (item?.objectType !== 'DICTIONARY' && item?.objectType !== 'MULTICOLUMNDICT' && INSIGHT_META_KEYS.has(item?.key)) {
            note(item.key, item.value);
        } else {
            note('activityCode', entries?.find(e => e.key === 'activityCode')?.value);
            items.push(item);
        }
    }
    return { items, meta };
}

/* --------------------------------------------------------------- endpoints */

export const campaignInsightsService = {
    async getOutputSummary(
        { campaignId, capsuleId, pageNumber, pageSize }: OutputScope & { pageNumber: number; pageSize: number }
    ): Promise<SummaryPage> {
        const response = await axios.post(
            getBaseUrl(),
            {
                data: { campaignId, capsuleId, view: 'summary', pageNumber, pageSize },
                agent: { groupBy: 'groupName' },
            },
            { headers: getHeaders() }
        );
        const body = response.data;
        const all: DataElement[] = Array.isArray(body?.data) ? body.data : [];
        const pageInfo = all.filter(isPageInfoRow);
        return {
            rows: all.filter(row => !pageInfo.includes(row)),
            total: totalOf(body) ?? pageInfoCount(pageInfo, 'totalGroups'),
        };
    },

    async getOutputGroup(
        { campaignId, capsuleId, groupIdentifier }: OutputScope & { groupIdentifier: string }
    ): Promise<Item[]> {
        const response = await axios.post(
            `${getBaseUrl()}/group`,
            { data: { campaignId, capsuleId, groupIdentifier } },
            { headers: getHeaders() }
        );
        return itemsOf(response.data?.data);
    },

    /** One insight by the id an agent reference carries; no campaign or capsule needed. */
    async getInsight(insightId: string): Promise<InsightDetail> {
        const response = await axios.post(
            `${getBaseUrl()}/insight`,
            { data: { insightId } },
            { headers: getHeaders() }
        );
        return insightOf(response.data?.data);
    },
};
